import type { TaskView } from '@/lib/api/generated/model';
import { runOutcome, type RunOutcome } from './executionHistory';
import { stepDisplayName } from './stepNames';

/**
 * A run as the drawer names it everywhere: its step, its outcome, and when
 * (spec 119 FR-005). The drawer never numbers runs, so "opened by run 3" is
 * "opened by Implement · Completed · 2 hours ago" — the words the Executions
 * and Results tabs already use for the same run.
 */
export interface RunLabel {
  name: string;
  outcome: RunOutcome;
  /** When it ended, else started, else was created; `null` if none is known. */
  when: string | null;
}

/**
 * Every run of the task, labelled once, keyed by execution id.
 *
 * Read from the task itself rather than the results read (research R3): the
 * results list only finished runs, and the run that opened a merge request
 * may be the one still going. The step is named by the id the run recorded,
 * as the Executions tab names it.
 */
export function runLabelIndex(
  task: Pick<TaskView, 'executions'>,
  stepLabels: ReadonlyMap<string, string> | undefined,
): ReadonlyMap<string, RunLabel> {
  return new Map(
    (task.executions ?? []).map((execution) => [
      execution.id,
      {
        name: stepDisplayName(execution.step_id_snapshot, stepLabels),
        outcome: runOutcome(execution.status),
        when:
          execution.ended_at ??
          execution.started_at ??
          execution.created_at ??
          null,
      },
    ]),
  );
}

/** One run's label, or `null` for a run the task does not know (or none). */
export function runLabel(
  executionId: string | null,
  task: Pick<TaskView, 'executions'>,
  stepLabels: ReadonlyMap<string, string> | undefined,
): RunLabel | null {
  if (executionId === null) return null;
  return runLabelIndex(task, stepLabels).get(executionId) ?? null;
}
